/** @jsx jsx */
import { jsx } from "theme-ui"
import { useCallback } from "react"
import { Global, css } from "@emotion/core"
import { Menu, MenuButton, MenuLink } from "@reach/menu-button"
import { useTranslation } from "react-i18next"
import "@reach/menu-button/styles.css"

import { isDefaultLng, getResolvedLocale } from "../../../utils/i18n"
import locales from "../../../../../locales"

import Dropdown from "./Dropdown"

const GlobalStyles = theme => css`
  [data-reach-menu-list],
  [data-reach-menu-item] {
    padding: 0;
  }

  [data-reach-menu] {
    font-family: inherit;
    z-index: 2;
  }

  button[data-reach-menu-item] {
    appearance: none;
    background: none;
    border: none;
    padding: inherit;
    width: 100%;
    outline: none;
  }

  [data-reach-menu-item][data-selected] {
    background: ${theme.colors.primary};
  }
`

const LanguageSwitcher = ({ isMobile, onChange, location, ...otherProps }) => {
  const { i18n } = useTranslation()
  const resolvedLocale = getResolvedLocale(i18n)
  const localesOrdered = [
    ...locales.filter(
      locale => locale.config.code !== resolvedLocale.config.code
    ),
    resolvedLocale,
  ]

  const handleLanguageChange = useCallback(
    e => {
      // Ugly hack to avoid a flash on language change
      localStorage.setItem("i18nextLng", e.currentTarget.dataset.lang)
      onChange && onChange()
    },
    [onChange]
  )

  return (
    <div
      sx={{
        position: isMobile ? "absolute" : "fixed",
        bottom: 0,
        left: isMobile ? "50%" : 0,
        transform: isMobile ? "translateX(-50%)" : "none",
        padding: isMobile ? "80px 20px" : "40px",
        zIndex: 1,
      }}
      {...otherProps}
    >
      <Global styles={GlobalStyles} />
      <Menu>
        <MenuButton
          sx={{
            variant: "text.caps",
            cursor: "pointer",
            userSelect: "none",
            outline: "none",
            padding: 3,
            background: "white",
            border: "none",
            boxShadow: "button",
            borderRadius: "19px",
            minWidth: "95px",
            '&:focus, &:hover, &[aria-expanded="true"]': {
              boxShadow: "card",
              color: "primary",
            },
            lineHeight: 1.15,
          }}
        >
          {resolvedLocale.config.label}
        </MenuButton>
        <Dropdown>
          {localesOrdered.map(locale => {
            let href

            if (isDefaultLng(locale.config.code)) {
              href = location.pathname.replace(
                `/${resolvedLocale.config.code}`,
                ""
              )
            } else {
              if (location.pathname === "/") {
                href = `/${locale.config.code}/`
              } else {
                href = location.pathname.replace(
                  resolvedLocale.config.code,
                  locale.config.code
                )
              }
            }

            if (isDefaultLng(resolvedLocale.config.code)) {
              href = `/${locale.config.code}${location.pathname}`
            }

            return (
              <MenuLink
                key={locale.config.code}
                as={locale === resolvedLocale ? "button" : "a"}
                href={href}
                onClick={handleLanguageChange}
                data-lang={locale.config.code}
              >
                <span
                  sx={{
                    variant: "text.caps",
                    padding: 3,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "inherit",
                    userSelect: "none",
                    lineHeight: 1.15,
                  }}
                >
                  {locale.config.label}
                </span>
              </MenuLink>
            )
          })}
        </Dropdown>
      </Menu>
    </div>
  )
}

export default LanguageSwitcher
